import { C8AttributeValue, C8Node, C8NodeType, CLASS_UOM_CONVERSION, cnl, getUOMConversionString, getUOMValueString, UOM_SUFFIX, VALUE_TYPE } from "c8-platform";
import { ControllerInterface } from "csi/action/Controller";
import comm from "csi/comm";
import Request from "csi/comm/Request";
import csiHeading from "csi/heading/_base";
import { validateValue } from "csi/middleware/rollup/rollupService";
import { ATTR_VALUETYPE, getClass } from "csi/reflection/reflection";
import ReflectionClass from "csi/reflection/ReflectionClass";
import siteDefaultLocale from "csi/siteDefaultLocale";
import types from "csi/types";
import { C8AttributeId, ICache } from "csi/typings";
import xml from "csi/xml";
import { ILocaleProvider } from "designSystem/locale";
import { each, map, some, uniq, values, without } from "lodash";
import BusinessObject from "src/SiteAdmin/BusinessObject";

// Object{Class[]}: the map of reflection classes to validate attribute Id with the key as a root reflection class name
const reflectionClassesForAttributeIdValidationMap = <ReflectionClass>{};

/** get owner class for an attribute for the given business and the owner class of the BO where it originates */
export function getOwnerClassForBusinessObject(nodeBO: C8Node, sourceOwnerClass: C8NodeType): C8NodeType {
	// if the owner class from the source BO is a revision, then select the revision class for this BO too
	return getClass(sourceOwnerClass).isRevision() ? nodeBO.AttributeClassName : nodeBO.RootClassName;
}

/** catch validation error from given function and return it, otherwise return empty string */
export function validateWithError(validateFunction: VoidFunction): string | undefined {
	try {
		validateFunction();
	} catch (ex) {
		return ex.message;
	}
}

/** validate attribute id */
export function validateAttributeId(
	locale: ILocaleProvider, 
	nodeCA: C8Node, 
	nodeBO: C8Node,
	ownerClass: C8NodeType,
	value: C8AttributeId, 
	cacheView: ICache, 
	bSubset?: boolean, 
	uomForm?: boolean,
): void {
	if (value.length > 0 && !isNaN(Number(value.substring(0, 1)))) {
		throw new Error(locale.resource("AttributeNameCanNotStartWithDigit"));
	}

	if (bSubset) {
		return;
	}

	if (nodeCA) {
		if (siteDefaultLocale.getConfigurableAttributeId(nodeCA) == value) {
			return;
		}
	}

	csiHeading.validateValue(value, VALUE_TYPE.STRING, types.FORMAT_ALPHANUMERIC, false, null, false);

	validateAttributeIdAgainstDomainClass(nodeBO, ownerClass, value, cacheView, locale);

	// prevent the creation of two attributes that have matching uom names (ex: 'Name' and 'NameUOM')
	if (value.endsWith(UOM_SUFFIX)) {
		validateAttributeIdAgainstDomainClass(nodeBO, ownerClass, getUOMValueString(value), cacheView, locale);
	} else {
		validateAttributeIdAgainstDomainClass(nodeBO, ownerClass, getUOMConversionString(value), cacheView, locale);
	}

	if (uomForm) {
		validateValueTypeOnBaseClassesRecursively(nodeBO.BaseBusinessObjects, getUOMConversionString(value), xml.ATTR_REF, CLASS_UOM_CONVERSION, cacheView, locale);
		validateValueTypeOnDerivedClasses(nodeBO, getUOMConversionString(value), xml.ATTR_REF, CLASS_UOM_CONVERSION, cacheView, locale);
	}
};

/**
 * make sure the attribute with 'attrId' doesn't exist on all classes
 * of domain class where 'className' belong
 */
function validateAttributeIdAgainstDomainClass(nodeBO: C8Node, ownerClass: C8NodeType, attrId: C8AttributeId, cacheView: ICache, locale: ILocaleProvider): void {
	const reflectionClassesForAttributeIdValidation = getReflectionClassesForAttributeIdValidation(ownerClass);
	const setUrls = new Set<string>();

	setUrls.add(nodeBO.$URL);

	reflectionClassesForAttributeIdValidation.forEach((rc) => {
		const boUrl = rc.getBusinessObject();

		if (boUrl) {
			setUrls.add(boUrl);
		}
	});

	const bos = cacheView.getNodes([...setUrls]);

	bos.forEach((bo) => {
		validateAttributeIdAgainstBusinessObject(bo, ownerClass, attrId, cacheView, locale);
	});
}

/**
 * throw exception if an attribute with 'attrId' exists in business object or and of its inherited business objects
 */
function validateAttributeIdAgainstBusinessObject(nodeBO: C8Node, sourceOwnerClass: C8NodeType, attrId: C8AttributeId, cacheView: ICache, locale: ILocaleProvider): void {
	const attrs = nodeBO.ConfigurableAttributes;
	if (attrs) {
		const ownerClass = getOwnerClassForBusinessObject(nodeBO, sourceOwnerClass);
		const caUrl = attrs[attrId] || attrs[ownerClass + "/" + attrId];
		if (caUrl) {
			throw new Error(locale.resource("CustomAttribute_ErrorDuplicate", [attrId, siteDefaultLocale.getTypeName(ownerClass)]));
		}
	}

	// recursively check inherited business objects
	const bos = cacheView.getNodes(nodeBO.BaseBusinessObjects);

	bos.forEach((bo) => {
		validateAttributeIdAgainstBusinessObject(bo, sourceOwnerClass, attrId, cacheView, locale);
	});
}

/**
 * add recursion to Value Type O nBaseClasses validation
 */
function validateValueTypeOnBaseClassesRecursively(boUrls: cnl[], name: string, type: string, targetClassName: C8NodeType, cacheView: ICache, locale: ILocaleProvider): void {
	const bos = cacheView.getNodes(boUrls);

	bos.forEach((bo) => {
		validateValueTypeAgainstBO(bo, "CustomAttribute_ErrorTypeDifferentFromBase", name, type, targetClassName, cacheView, locale);
		validateValueTypeOnBaseClassesRecursively(bo.BaseBusinessObjects, name, type, targetClassName, cacheView, locale);
	});
}

/**
 * validate value type on derived classes
 */
function validateValueTypeOnDerivedClasses(currentBO: C8Node, name: string, type: string, targetClassName: string, cacheView: ICache, locale: ILocaleProvider): void {
	const bos = getDerivedBusinessObjects(currentBO, cacheView);

	bos.forEach((bo: C8Node) => {
		validateValueTypeAgainstBO(bo, "CustomAttribute_ErrorTypeDifferentFromDerived", name, type, targetClassName, cacheView, locale);
	});
}

/**
 * validate attribute Id with the key as a root reflection class name
 */
function getReflectionClassesForAttributeIdValidation(rootClassName: string): ReflectionClass[]{
	let reflectionClasses = reflectionClassesForAttributeIdValidationMap[rootClassName];

	if (reflectionClasses == null) {
		reflectionClasses = [];

		// go to a domain class of root class
		const rootClass = getClass(rootClassName);
		const domainClass = rootClass.domainClass;
		if (domainClass != null) {
			// Get reflection classes from domain class excluding the root class of business object.
			// We exclude a root class for now to avoid collecting base classes of root class because
			// we need to allow to have an attribute with the same Id as on a base class of root class
			let reflectionClassesFromDomainClass =values(domainClass.secondaries);
			reflectionClassesFromDomainClass.push(domainClass.primary);
			reflectionClassesFromDomainClass = without(reflectionClassesFromDomainClass, rootClass);

			// get all reflection classes including base ones
			function reflectionClassCollector(rc: ReflectionClass): void {
				reflectionClasses.push(rc);
			}
			each(reflectionClassesFromDomainClass, (rc: ReflectionClass) => {
				rc.traverse(true, false, reflectionClassCollector);
			});

			// add a root class back to validate against it
			reflectionClasses.push(rootClass);
			reflectionClasses = uniq(reflectionClasses);
		}

		reflectionClassesForAttributeIdValidationMap[rootClassName] = reflectionClasses;
	}

	return reflectionClasses;
}

/**
 * if the attribute with the same name exists on the passed BO and the type of attribute is different, throw an exception
 */
function validateValueTypeAgainstBO(bo:C8Node, errorMessageResource: string, name: string, type: string, targetClassName: string, cacheView: ICache, locale: ILocaleProvider): void{
	const ca = getConfigurableAttributeFromBOByName(bo, name, cacheView);
	if (!ca) {
		return;
	}

	// compare types
	const typeOfExistingAttr = ca[ATTR_VALUETYPE];
	if (type != typeOfExistingAttr) {
		throw new Error(locale.resource(errorMessageResource, [typeOfExistingAttr, name, locale.getNodeName(bo)]));
	}

	// compare target classes
	let bCorrectTargetClassName = true;
	let classNames = null;
	const existingTargetClassName = ca[BusinessObject.ATTR_TARGET_CLASS_NAME];
	if (existingTargetClassName) {
		bCorrectTargetClassName = (targetClassName == existingTargetClassName);
		classNames = [existingTargetClassName];
	}
	// DE27610: can't change target class on BuiltIn attributes. So removed the validation for it.

	if (!bCorrectTargetClassName) {
		classNames = map(classNames, (clsName) => {return getDisplayableClassName(clsName, locale);});
		throw new Error(locale.resource("CustomAttribute_ErrorTypeDifferentTargetClass", [classNames.join(", "), name, locale.getNodeName(bo)]));
	}
}

/**
 * given the business object, return business objects from all derived classes
 */
function getDerivedBusinessObjects(bo: C8Node, cache: ICache): C8Node[] {
	const setUrls = new Set<string>();

	bo.RelatedClassNames.forEach((clsName: string) => {
		const currentRC = getClass(clsName);
		currentRC.traverse(false, true, (rc) => {
			if (rc == currentRC) {
				return;
			}

			const boUrl = rc.getBusinessObject();

			if (boUrl) {
				setUrls.add(boUrl);
			}
		});
	});

	const bos = cache.getNodes([...setUrls]);
	return bos;
}

/**
 * get configurable attribute from business object by name
 */
function getConfigurableAttributeFromBOByName(bo: C8Node, name: string, cache: ICache ): C8Node {
	let ca = null;
	const attrs = bo.ConfigurableAttributes;
	if (attrs) {
		let caUrl = attrs[name];
		if (!caUrl) {
			some(bo.RelatedClassNames, (clsName) => {
				caUrl = attrs[clsName + "/" + name];
				return caUrl;
			});
		}

		if (caUrl) {
			ca = cache.get(caUrl);
			if (!ca) {
				console.error("Cannot load the ConfigurableAttribute URL=", caUrl);
			}
		}
	}
	return ca;
}

/**
 * get displayable class name
 */
function getDisplayableClassName(clsName: string, locale: ILocaleProvider): string {
	return locale.type(clsName) + " (" + clsName + ")";
}

/**
 * Add necessary URLs from domain class of business object
 */
export function getRootRequest(controller: ControllerInterface): Request {
	let urls = [];

	// preload all BOs from reflection classes
	const boNode = controller.getParent();

	each(boNode.RelatedClassNames, (className) => {
		const reflectionClasses = getReflectionClassesForAttributeIdValidation(className);
		each(reflectionClasses, (rc) => {
			const v = rc.getBusinessObject();
			if (xml.isValidURL(v)) {
				urls.push(v);
			}
		});
	});

	urls = uniq(urls);

	let req = null;

	if (urls.length) {
		req = comm.getNodeRequest(urls, null, true);
	}

	return req;
}

/**
 * validate Initial Value
 */
export function validateInitialValue(value: C8AttributeValue, type: string, format: string ): void {
	csiHeading.validateValue(value, type, format, false, null, false);	
}


/**
 * validate if supplied string value meat "required" condition
 */
export function validateRequiredStringValue(locale: ILocaleProvider, value: string): void {
	if (!value) {
		throw new Error(locale.resource("RequiredValue"));
	}
}

/**
 * validate ValidationExpression field. This function is UI part of validation. Server will also validate it
 * NOTE: user should use server side JS syntax for this field, but this will be validated on server
 */
export function validateValidationExpression(locale: ILocaleProvider, value: C8AttributeValue, validationExpression: string): void {
	const validationResult = validateValue(String(value), validationExpression);
	if (validationResult === false) {
		throw new Error(locale.resource("CustomAttribute_ErrorInitialValueDoesntFulfillValidationExpression"));
	}
}

/**
 * validate CopyOptionsName field. It should be non empty and consist of two dot separated strings
 */
export function validateCopyOptions(locale: ILocaleProvider, value: string): void {
	if (!value || value.length == 0 || value.indexOf(".") < 0) {
		throw new Error(locale.resource("ERROR_AN_INVALID_COPY_OPTION_NAME_FORMAT"));
	}
}

/**
 * validate attrSelectEnumeration field. It should be non empty item
 */
export function validateSelectEnumeration(locale: ILocaleProvider, value: string): void {
	if (!value) {
		throw new Error(locale.resource("RequiredValue"));
	}
}